import React, { useEffect, useMemo, useState } from "react";
import { Form } from "antd";
import moment from "moment";
import { omit } from "lodash";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { useAppDispatch, useAppSelector } from "../..";
import { updateAccountProperty } from "../../store/slices/accounts";

export const AccountTypeVersionsTable = () => {
  const dispatch = useAppDispatch();
  const {
    account,
    accountDirectProperties,
    accountDirectPropertiesLoading,
    accountTypePropertyVersions,
    accountTypePropertyVersionsLoading,
  } = useAppSelector((state) => state.accounts);

  const { accountTypes } = useAppSelector((state) => state.dicts);
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  console.log({ childTableData });
  
  const [form] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const { getAccountTypePropertyVersions } = useAccountInfoContainerContext();

  const accountTypeProperty = useMemo(
    () => accountDirectProperties.find(({ typeId }) => typeId === "account_type"),
    [accountDirectProperties]
  );

  useEffect(() => {
    if (accountTypeProperty) {
      setTableData([
        {
          key: accountTypeProperty.id,
          id: accountTypeProperty.id,
          accountTypeName: versionsTableUtils.geDicValueById(
            accountTypes,
            accountTypeProperty.dictionaryValue
          )?.name,
          fromDate: accountTypeProperty.fromDate,
          toDate: accountTypeProperty.toDate,
        },
      ]);
    }
  }, [accountTypeProperty, accountTypes]);

  useEffect(() => {
    if (checkIsLoaded(accountTypePropertyVersionsLoading)) {
      const childData = (accountTypePropertyVersions as any)
        .map((version, i) => ({
          ...version,
          ...versionsTableUtils.getCommonChildTableProps(
            version,
            i,
            accountTypePropertyVersions.length
          ),
        }))
        .sort(versionsTableUtils.sortVersionsByDate);
      setChildTableData(childData);
    }
  }, [accountTypePropertyVersionsLoading, accountTypePropertyVersions]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    accountTypeName: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Тип",
      dataIndex: "accountTypeName",
      key: "accountTypeName",
      width: columnsWidths.accountTypeName,
      hidden: false,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      hidden: false,
      render(text, record) {
        return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Статус",
      dataIndex: "accountTypeName",
      key: "accountTypeName",
      width: columnsWidths.accountTypeName,
      hidden: false,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={accountTypes}
          name="dictionaryValue"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        // const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        const isHasAccess = true; // TODO Eugeniy Sumaev - добавить привилегии когда появятся
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden, accountTypes]);

  const onFinish = ({ fromDate, dictionaryValue }) => {
    const updatedVersion = versionsTableUtils.getChangeableRecord(childTableData);
    // console.log(updatedVersion);
    const force = updatedVersion.isEditing;
    const date = (() => {
      if (force) {
        return updatedVersion.fromDate;
      }
      if (moment(fromDate).isSame(updatedVersion.fromDate, "day")) {
        return toHttpFormat(moment(updatedVersion.fromDate).add(1, "second"));
      }
      return toHttpFormatStartOfDay(fromDate);
    })();

    const property = {
      ...omit(updatedVersion, [
        "key",
        "isCreated",
        "isEditing",
        "isCurrentVersion",
        "isLatestVersion",
        "isPastVersion",
      ]),
      dictionaryValue,
      fromDate: date,
    };

    dispatch(
      // @ts-ignore
      updateAccountProperty({
        accountId: account.id,
        force,
        property,
      })
    );
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <h4 style={{ marginTop: "45px" }}>Тип оператора</h4>
      <ToggleableVersionsTable
        tableName="account-type-versions-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        isTableLoading={checkIsLoading(accountDirectPropertiesLoading)}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(accountTypePropertyVersionsLoading)}
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={({ id }) => {
          getAccountTypePropertyVersions(id);
        }}
      />
    </Form>
  );
};
